import { Link, useLocation, Outlet } from 'react-router-dom';
import Header from './Header';
import { useAuth } from '../context/AuthContext';

// Base admin navigation (feature-flags handled specially below)
const baseNav = [
  { path: '/admin', label: 'Dashboard', icon: '📊' },
  { path: '/admin/tiers', label: 'Tiers', icon: '🎯' },
  { path: '/admin/users', label: 'Users', icon: '👥' },
  { path: '/admin/newsletter', label: 'Newsletter', icon: '📰' },
  // Denied Tags moved into Settings tab; keep it accessible only via Admin Settings
  { path: '/admin/reports', label: 'Reports', icon: '📈' },
  { path: '/admin/audit', label: 'Audit Log', icon: '📋' },

  { path: '/admin/settings', label: 'Settings', icon: '⚙️' },

];

function AdminLayout() {
  const location = useLocation();

  // useAuth throws when not wrapped in AuthProvider (unit tests). Catch
  // that situation and fall back to a null user so tests don't fail.
  let user = null;
  try {
    const auth = useAuth();
    user = auth?.user || null;
  } catch (e) {
    user = null;
  }

  // Build navItems from baseNav; append Feature Flags as the last
  // entry and only show it to root admins (those defined in ADMIN_USER_IDS)
  const navItems = [...baseNav];
  if (user?.isRootAdmin) {
    navItems.push({ path: '/admin/features', label: 'Feature Flags', icon: '🚩' });
  }

  // Generate breadcrumbs from path
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbs = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const label = segment.charAt(0).toUpperCase() + segment.slice(1);
    return { path, label };
  });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Header */}
      <Header />

      <div className="flex">
        {/* Sidebar */}

        <aside className="w-64 bg-dark-800 border-r border-dark-700 shrink-0 h-screen flex flex-col">

        <div className="p-6">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <span role="img" aria-label="Admin">👑</span>
            Admin Panel
          </h1>
        </div>

        <nav aria-label="Admin navigation" className="px-3 flex-1 overflow-auto">
          <ul role="list">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`
                      flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors
                      ${isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-dark-700'
                      }
                    `}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span role="img" aria-hidden="true" className="text-lg">
                      {item.icon}
                    </span>
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Breadcrumbs */}
        <header className="bg-dark-800 border-b border-dark-700 px-6 py-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm">
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return (
                  <li key={crumb.path} className="flex items-center gap-2">
                    {index > 0 && (
                      <span className="text-gray-600" aria-hidden="true">
                        /
                      </span>
                    )}
                    {isLast ? (
                      <span className="text-white font-medium" aria-current="page">
                        {crumb.label}
                      </span>
                    ) : (
                      <Link
                        to={crumb.path}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;

